import { motion } from "framer-motion";
import { ChevronDown, MapPin, Clock, Sparkles, Car, Phone, Zap, CheckCircle, Heart } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const HeroSection = () => {
  const { t } = useLanguage();
  const whatsappNumber = "+905488484828";
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/\+/g, "")}?text=${encodeURIComponent(t("floatingWhatsapp.defaultMessage"))}`;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1603562767384-c8fe55858c2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwcGFydHklMjBkYW5jZXIlMjBzaWxob3VldHRlfGVufDB8fHx8MTc3OTQwNzkxMnww&ixlib=rb-4.1.0&q=85&w=1920"
          alt="First Lady - Konsomatris ve Dansöz Hizmeti Kıbrıs"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Floating elements */}
      <div className="absolute top-1/4 left-10 w-2 h-2 bg-[#FF00FF] rounded-full breathing" />
      <div className="absolute top-1/3 right-20 w-3 h-3 bg-[#00FFFF] rounded-full breathing" style={{ animationDelay: "1s" }} />
      <div className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-[#D4AF37] rounded-full breathing" style={{ animationDelay: "2s" }} />

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        {/* Urgency Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-red-500/20 border border-red-500/50"
        >
          <Zap size={14} className="text-red-400" />
          <span className="text-xs font-bold tracking-wider text-red-400 uppercase">{t("hero.urgencyBanner")}</span>
        </motion.div>

        {/* Location Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 border border-[#FF00FF]/50 bg-black/40 backdrop-blur-sm ml-2"
        >
          <MapPin size={14} className="text-[#FF00FF]" />
          <span className="text-xs font-mono tracking-[0.2em] text-[#FF00FF] uppercase">{t("hero.location")}</span>
        </motion.div>

        {/* Main H1 Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-['Syne'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter uppercase leading-none mb-4"
          data-testid="hero-title"
        >
          <span className="block text-[#D4AF37] neon-text-gold">{t("hero.title1")}</span>
          <span className="block text-white text-3xl sm:text-4xl md:text-5xl mt-2">{t("hero.title2")}</span>
          <span className="block text-lg sm:text-xl md:text-2xl mt-2 text-[#FF00FF] font-normal tracking-widest">{t("hero.title3")}</span>
        </motion.h1>

        {/* H2 Subtitle */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-4 leading-relaxed font-normal"
          data-testid="hero-subtitle"
        >
          {t("hero.subtitle")}
        </motion.h2>

        {/* Trust Signals */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="flex flex-wrap justify-center gap-4 mb-6 text-sm"
        >
          <span className="inline-flex items-center gap-1 text-[#D4AF37]">
            <CheckCircle size={14} /> {t("hero.trustSignal1")}
          </span>
          <span className="inline-flex items-center gap-1 text-[#FF00FF]">
            <CheckCircle size={14} /> {t("hero.trustSignal2")}
          </span>
          <span className="inline-flex items-center gap-1 text-[#00FFFF]">
            <CheckCircle size={14} /> {t("hero.trustSignal3")}
          </span>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 text-sm text-gray-400"
        >
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 border border-white/10">
            <Heart size={16} className="text-[#FF00FF]" />
            <span>{t("hero.feature1")}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 border border-white/10">
            <Car size={16} className="text-[#D4AF37]" />
            <span>{t("hero.feature2")}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 border border-white/10">
            <Clock size={16} className="text-[#00FFFF]" />
            <span>{t("hero.feature3")}</span>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          {/* Primary CTA - WhatsApp */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center gap-3 bg-[#25D366] text-white px-10 py-5 font-bold text-lg tracking-wider uppercase hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(37,211,102,0.5)] btn-clip w-full sm:w-auto justify-center"
            data-testid="hero-whatsapp-btn"
          >
            <FaWhatsapp size={26} />
            <span>{t("hero.ctaWhatsapp")}</span>
            <span className="absolute inset-0 bg-white/20 animate-ping opacity-20 pointer-events-none"></span>
          </a>
          
          {/* Secondary CTA - Call */}
          <a
            href="tel:+905488484828"
            className="group inline-flex items-center gap-3 bg-[#FF00FF] text-white px-10 py-5 font-bold text-lg tracking-wider uppercase hover:scale-105 transition-all duration-300 neon-glow btn-clip w-full sm:w-auto justify-center"
            data-testid="hero-call-btn"
          >
            <Phone size={24} />
            <span>{t("hero.ctaCall")}</span>
          </a>
        </motion.div>

        {/* Phone Number Display */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6"
        >
          <a 
            href="tel:+905488484828" 
            className="font-['Bebas_Neue'] text-3xl md:text-4xl text-white hover:text-[#D4AF37] transition-colors tracking-wider"
          >
            +90 548 848 4828
          </a>
          <p className="text-gray-500 text-xs mt-2 uppercase tracking-wider">{t("hero.available")}</p>
        </motion.div>

        {/* Instant response badge */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-4 inline-flex items-center gap-2 text-green-400 text-sm"
        >
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          {t("hero.onlineNow")}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-gray-500"
        >
          <span className="text-xs tracking-[0.3em] uppercase">{t("hero.discover")}</span>
          <ChevronDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
